import type { MeetingProvider } from "@/domains/calendar/getMeetingPlatformFromUrl";
import type { ChatModel, ChatModelFamily } from "@/domains/chat-model";
import type { AudioRecordingStrategy } from "@/store/audio-recordings/types";
import type { AgentSurfaceId } from "@/store/agent-overlay/types";
import type { GatedFeature } from "@/store/feature-gates/types";
import type {
  OneClickImportEntryPoint,
  OneClickImportSource,
} from "@/store/one-click-import/types";
import type { SubscriptionPlanKind, SubscriptionPlanVariant } from "@/store/subscriptions/types";
import { EventContext } from "./context";

export enum TrackedEvent {
  NoteView = "note_view",
  NotesView = "notes_view",
  ImportedNotesView = "imported_notes_view",
  ImportViaCreateNote = "import_via_create_note",
  ImportViaUserProfileMenu = "import_via_user_profile_menu",
  NoteCreate = "note_create",
  NoteAddToCollection = "note_add_to_collection",
  AutoOrganizeNote = "auto_organize_note",
  NoteShare = "note_share",
  TemplateCreate = "template_create",
  TemplateUsed = "template_used",
  TemplateView = "template_view",
  TemplatesView = "templates_view",
  CollectionView = "collection_view",
  CollectionsView = "collections_view",
  ImportedCollectionsView = "imported_collections_view",
  CollectionCreate = "collection_create",
  CollectionShare = "collection_share",
  TemplateShare = "template_share",
  ChatView = "chat_view",
  ChatCreate = "chat_create",
  ChatCreateFromConversationTooLong = "chat_create_from_conversation_too_long",
  ChatResume = "chat_resume",
  ChatSelect = "chat_select",
  ChatMessageSend = "chat_message_send",
  ChatMessageSendFromSuggestion = "chat_message_send_from_suggestion",
  ChatMessageUpvote = "chat_message_upvote",
  ChatMessageDownvote = "chat_message_downvote",
  ChatInputLookAtClick = "chat_input_look_at_click",
  ChatInputAgentModeToggle = "chat_input_agent_mode_toggle",
  ChatInputContextAdd = "chat_input_context_add",
  ChatInputContextRemove = "chat_input_context_remove",
  SearchView = "search_view",
  SearchResultsOpenNote = "search_results_open_note",
  SearchResultsOpenCollection = "search_results_open_collection",
  ViewNoteAttachmentPdf = "view_note_attachment_pdf",
  QuickSearchView = "quick_search_view",
  QuickSearchOpenCollection = "quick_search_open_collection",
  QuickSearchOpenNote = "quick_search_open_note",
  ExportCreate = "export_create",
  PerformanceHeartbeat = "performance_heartbeat",
  SyncErrorShown = "sync_error_shown",
  AppCalloutBannerView = "app_callout_banner_view",
  AppCalloutBannerActionClicked = "app_callout_banner_action_clicked",
  AppCalloutBannerDismissed = "app_callout_banner_dismissed",
  PerformanceBootstrap = "performance_bootstrap",
  AppStoreInitErrorShown = "app_store_init_error_shown",
  ImportFilesDropped = "import_files_dropped",
  ImportFilesBatchStarted = "import_files_batch_started",
  ImportFilesBatchFailed = "import_files_batch_failed",
  KeyboardShortcutPressed = "keyboard_shortcut_pressed",

  // Note actions
  NoteCopyAsMarkdown = "note_copy_as_markdown",
  NotePasteMarkdown = "note_paste_markdown",

  // MaaC (Mem-as-a-Collaborator) Phase 1
  MemAttributionEditableToggle = "mem_attribution_editable_toggle",
  NoteShareSheetOpen = "note_share_sheet_open",

  // Email Capture Events
  EmailConnect = "email_connect",
  EmailDisconnect = "email_disconnect",
  EmailFormattingModeSelect = "email_formatting_mode_select",
  EmailFormattingSmartModeCustomize = "email_formatting_smart_mode_customize",
  EmailOrganizingModeChecked = "email_organizing_mode_checked",
  EmailOrganizingModeUnchecked = "email_organizing_mode_unchecked",
  EmailOrganizingSpecificCollectionsSaved = "email_organizing_specific_collections_saved",
  EmailOrganizingSmartModeCustomize = "email_organizing_smart_mode_customize",

  // Text Message Events
  TextFormattingModeSelect = "text_formatting_mode_select",
  TextFormattingSmartModeCustomize = "text_formatting_smart_mode_customize",
  TextOrganizingModeChecked = "text_organizing_mode_checked",
  TextOrganizingModeUnchecked = "text_organizing_mode_unchecked",
  TextOrganizingSpecificCollectionsSaved = "text_organizing_specific_collections_saved",
  TextOrganizingSmartModeCustomize = "text_organizing_smart_mode_customize",

  // API Request Events
  ApiKeyCreate = "api_key_create",
  ApiKeyName = "api_key_name",
  ApiKeyDelete = "api_key_delete",
  ApiDevDocsLinkClick = "api_dev_docs_link_click",

  // Zapier Events
  ZapierIntegrationLinkClick = "zapier_integration_link_click",
  ZapierDisconnect = "zapier_disconnect",

  // Voice Session Events
  VoiceSessionStart = "voice_session_start",
  VoiceSessionPause = "voice_session_pause",
  VoiceSessionResume = "voice_session_resume",
  VoiceSessionDone = "voice_session_done",
  VoiceSessionSuggestedEditsView = "voice_session_suggested_edits_view",
  VoiceSessionSuggestedEditsRefine = "voice_session_suggested_edits_refine",
  VoiceSessionSuggestedEditsAccept = "voice_session_suggested_edits_accept",
  VoiceSessionSuggestedEditsReject = "voice_session_suggested_edits_reject",
  VoiceRecordingView = "voice_recording_view",
  VoiceRecordingAudioPlay = "voice_recording_audio_play",
  VoiceRecordingTranscriptScroll = "voice_recording_transcript_scroll",
  DesktopSystemAudioPermissionsModalView = "desktop_system_audio_permissions_modal_view",
  DesktopSystemAudioPermissionInteract = "desktop_system_audio_permission_interact",
  DesktopSystemAudioPermissionOpenPreferences = "desktop_system_audio_permission_open_preferences",
  DesktopSystemAudioPermissionsRestartClick = "desktop_system_audio_permissions_restart_click",

  // Received from Electron
  MeetingDetectionNotificationView = "meeting_detection_notification_view",
  MeetingDetectionNotificationHover = "meeting_detection_notification_hover",
  MeetingDetectionNotificationDismiss = "meeting_detection_notification_dismiss",
  MeetingDetectionNotificationVoiceSessionStart = "meeting_detection_notification_voice_session_start",

  // Note Cleanup Events
  NoteCleanupStart = "note_cleanup_start",
  NoteCleanupRefine = "note_cleanup_refine",
  NoteCleanupAccept = "note_cleanup_accept",
  NoteCleanupReject = "note_cleanup_reject",

  // Welcome & Onboarding
  WelcomeOverlayView = "welcome_overlay_view",
  NativeUpsellView = "native_upsell_view",
  NativeUpsellSkip = "native_upsell_skip",
  NativeUpsellDownload = "native_upsell_download",
  ReferrerStepComplete = "referrer_step_complete",
  NewUserOnboardingViewed = "new_user_onboarding_viewed",
  OnboardingVoiceModeView = "onboarding_voice_mode_view",
  OnboardingVoiceModeOptionSelect = "onboarding_voice_mode_option_select",
  OnboardingVoiceModeSkip = "onboarding_voice_mode_skip",
  OnboardingDownloadClick = "onboarding_download_click",
  OnboardingStepChange = "onboarding_step_change",
  OnboardingSubstepChange = "onboarding_substep_change",
  OnboardingComplete = "onboarding_complete",
  IntentOnboardingPickerView = "intent_onboarding_picker_view",
  IntentOnboardingIntentSelect = "intent_onboarding_intent_select",
  IntentOnboardingNativeResume = "intent_onboarding_native_resume",
  IntentOnboardingTrialPitchView = "intent_onboarding_trial_pitch_view",
  IntentOnboardingTrialStartClick = "intent_onboarding_trial_start_click",

  ImportCtaView = "import_cta_view",
  ImportWizardView = "import_wizard_view",
  ImportWizardAppSelect = "import_wizard_app_select",
  ImportWizardSkip = "import_wizard_skip",
  ImportWizardStepChange = "import_wizard_step_change",
  ImportWizardComplete = "import_wizard_complete",

  // OneClick Import Events
  OneClickImportListView = "one_click_import_list_view",
  OneClickImportItemSelect = "one_click_import_item_select",
  OneClickImportItemDeselect = "one_click_import_item_deselect",
  OneClickImportCancel = "one_click_import_cancel",
  OneClickImportSubmit = "one_click_import_submit",
  OneClickImportComplete = "one_click_import_complete",
  OneClickImportLoadFailed = "one_click_import_load_failed",

  // Multi-Tab Events
  PaneSplit = "pane_split",
  TabOpen = "tab_open",
  TabView = "tab_view",

  //Chrome Extension
  ChromeExtensionOnboardingInstallerView = "chrome_extension_onboarding_installer_view",
  ChromeExtensionOnboardingInstallerClick = "chrome_extension_onboarding_installer_click",
  ChromeExtensionHomeCarouselInstallerClick = "chrome_extension_home_carousel_installer_click",
  ChatGlobalInstructionsSave = "chat_global_instructions_save",
  ChromeExtensionCustomInstructionsSave = "chrome_extension_custom_instructions_save",
  ChromeExtensionModelSelect = "chrome_extension_model_select",
  ChromeExtensionModelDropdownOpen = "chrome_extension_model_dropdown_open",

  // Session Chat
  ChatSessionMessageSend = "chat_session_message_send",
  ChatSessionMessageUpvote = "chat_session_message_upvote",
  ChatSessionMessageDownvote = "chat_session_message_downvote",
  ChatPanelView = "chat_panel_view",
  ChatModelSelect = "chat_model_select",
  ChatModelDropdownOpen = "chat_model_dropdown_open",
  SidePanelView = "side_panel_view",

  // Monetization / Paywall
  PaywallView = "paywall_view",
  PaywallCancelClick = "paywall_cancel_click",
  PaywallUpgradeClick = "paywall_upgrade_click",
  PaywallUpgradeReturn = "paywall_upgrade_return",
  PaywallUpgradeFailed = "paywall_upgrade_failed",
  PaywallCancelReturn = "paywall_cancel_return",
  FeatureGateBannerView = "feature_gate_banner_view",
  FeatureGateBannerUpgradeClick = "feature_gate_banner_upgrade_click",
  FloatingAgentProactiveTrialBannerView = "floating_agent_proactive_trial_banner_view",
  FloatingAgentProactiveTrialBannerClicked = "floating_agent_proactive_trial_banner_clicked",
  FloatingAgentProactiveTrialBannerDismissed = "floating_agent_proactive_trial_banner_dismissed",
  FloatingAgentUpgradeButtonClicked = "floating_agent_upgrade_button_clicked",
  FloatingAgentPresenceSet = "floating_agent_presence_set",
  MemAgentPttStart = "mem_agent_ptt_start",
  MemAgentPttEnd = "mem_agent_ptt_end",
  FeatureGateDenied = "feature_gate_denied",

  // Growth conversion funnel
  AccountSignupCompleted = "account_signup_completed",
  EmailPasswordAuthFunnel = "email_password_auth_funnel",
  MobileWebLoginCompleted = "mobile_web_login_completed",
  MemAgentMessageSent = "mem_agent_message_sent",
  ProactiveAccessEnabled = "proactive_access_enabled",

  // Mem Agent Setup
  MemAgentOverlayEntryClick = "mem_agent_overlay_entry_click",
  MemAgentOverlayView = "mem_agent_overlay_view",
  MemAgentOverlayClose = "mem_agent_overlay_close",
  MemAgentOverlayItemClick = "mem_agent_overlay_item_click",
  MemAgentOverlayFilterChange = "mem_agent_overlay_filter_change",
  MemAgentOverlayTaskStatusToggle = "mem_agent_overlay_task_status_toggle",
  MemAgentSettingsPageView = "mem_agent_settings_page_view",
  MemAgentSlackConnectRedirect = "mem_agent_slack_connect_redirect",
  MemAgentSlackConnectComplete = "mem_agent_slack_connect_complete",
  MemAgentSlackConnectFailed = "mem_agent_slack_connect_failed",
  MemAgentSlackConnectedModalView = "mem_agent_slack_connected_modal_view",
  MemAgentSlackDisconnect = "mem_agent_slack_disconnect",
  MemAgentSlackOpenClick = "mem_agent_slack_open_click",
  MemAgentLearnMoreClick = "mem_agent_learn_more_click",
  MemAgentChannelConnectAttempt = "mem_agent_channel_connect_attempt",
  MemAgentChannelConnectActivationReady = "mem_agent_channel_connect_activation_ready",
  MemAgentChannelConnectOpen = "mem_agent_channel_connect_open",
  MemAgentChannelConnectComplete = "mem_agent_channel_connect_complete",
  MemAgentChannelConnectFailed = "mem_agent_channel_connect_failed",
  MemAgentChannelConnectDialogClosed = "mem_agent_channel_connect_dialog_closed",
  MemAgentIMessageLeadClaimStart = "mem_agent_imessage_lead_claim_start",
  MemAgentIMessageLeadClaimComplete = "mem_agent_imessage_lead_claim_complete",
  MemAgentIMessageLeadClaimFailed = "mem_agent_imessage_lead_claim_failed",
  MemAgentShareDialogOpen = "mem_agent_share_dialog_open",
  MemAgentShareLinkCreate = "mem_agent_share_link_create",
  MemAgentShareLinkCopy = "mem_agent_share_link_copy",
  MemAgentShareLinkRevoke = "mem_agent_share_link_revoke",
  MemAgentShareLinkView = "mem_agent_share_link_view",
  MemAgentShareLinkUnavailable = "mem_agent_share_link_unavailable",
  MemAgentShareAccept = "mem_agent_share_accept",
  MemAgentShareAcceptFailed = "mem_agent_share_accept_failed",
  MemAgentSkillCreate = "mem_agent_skill_create",
  MemAgentSkillUpdate = "mem_agent_skill_update",
  MemAgentSkillDelete = "mem_agent_skill_delete",
  McpConnectionSetupLifecycle = "mcp_connection_setup_lifecycle",
  RemindersCalloutVisible = "reminders_callout_visible",
  RemindersSettingsPageVisible = "reminders_settings_page_visible",
  RemindersEnableClicked = "reminders_enable_clicked",
  ReminderShillSetupDialogVisible = "reminder_shill_setup_dialog_visible",
  EditorReminderShillPillVisible = "editor_reminder_shill_pill_visible",
  EditorReminderShillPillDismissed = "editor_reminder_shill_pill_dismissed",
  EditorReminderShillPillSettingsLaunched = "editor_reminder_shill_pill_settings_launched",

  // Heads Up
  HeadsUpItemHover = "heads_up_item_hover",
  HeadsUpItemClick = "heads_up_item_click",
  HeadsUpRefreshClick = "heads_up_refresh_click",
  HeadsUpRegenerateClick = "heads_up_regenerate_click",
  HeadsUpFindMoreClick = "heads_up_find_more_click",
  HeadsUpLiveSessionStart = "heads_up_live_session_start",
  HeadsUpLiveSessionEnd = "heads_up_live_session_end",
  HeadsUpLiveEntryAppear = "heads_up_live_entry_appear",
  HeadsUpLiveExplainerOpen = "heads_up_live_explainer_open",
  HeadsUpLiveEntryFeedbackSubmit = "heads_up_live_entry_feedback_submit",

  // App and UI Readiness
  AppStoreInitialize = "app_store_initialize",
  UiReady = "ui_ready",
  LegacyBrowserBlocked = "legacy_browser_blocked",

  // Standrdized Experiment
  ExperimentStarted = "experiment_started",

  // Getting Started Guide
  GettingStartedStepCompleted = "getting_started_step_completed",
  GettingStartedDismissed = "getting_started_dismissed",
  GettingStartedToggled = "getting_started_toggled",
  GettingStartedAllComplete = "getting_started_all_complete",
  GettingStartedOnboardingTooltipDismissed = "getting_started_onboarding_tooltip_dismissed",

  // Editor
  ClipboardPaste = "clipboard_paste",
  EditorImageHoverToolbarOpen = "editor_image_hover_toolbar_open",
  EditorImageHoverToolbarDownload = "editor_image_hover_toolbar_download",
  OcrMenuOpen = "ocr_menu_open",
  OcrInsertClick = "ocr_insert_click",

  // System Permissions
  MeetingNotificationsSetupView = "meeting_notifications_setup_view",
  MeetingNotificationsSetupSubmit = "meeting_notifications_setup_submit",
  VoiceSetupView = "voice_setup_view",
  VoiceSetupSubmit = "voice_setup_submit",
  CalendarConnectStart = "calendar_connect_start",
  CalendarConnectComplete = "calendar_connect_complete",
  CalendarConnectFailed = "calendar_connect_failed",
  CalendarDisconnectStart = "calendar_disconnect_start",
  CalendarDisconnectComplete = "calendar_disconnect_complete",
  CalendarToggle = "calendar_toggle",
  CalendarRefreshManual = "calendar_refresh_manual",
  NoteLinkToCalendarEvent = "note_link_to_calendar_event",
  CalendarEventJoin = "calendar_event_join",
  CalendarEventOpen = "calendar_event_open",
  CalendarIntegrationShillHide = "calendar_integration_shill_hide",

  // Onboarding - Calendar Setup
  OnboardingCalendarSetupSkipped = "onboarding_calendar_setup_skipped",
  OnboardingCalendarSetupComplete = "onboarding_calendar_setup_complete",

  // Meeting Briefing
  MeetingBriefingView = "meeting_briefing_view",
  MeetingBriefingRegenerate = "meeting_briefing_regenerate",
  MeetingBriefingFeedbackSubmit = "meeting_briefing_feedback_submit",
  MeetingBriefingNoteChipInteract = "meeting_briefing_note_chip_interact",

  // PDF Viewer
  PdfViewerOpen = "pdf_viewer_open",
  PdfViewerPageChange = "pdf_viewer_page_change",
  PdfViewerEmbedToggle = "pdf_viewer_embed_toggle",
  PdfViewerClose = "pdf_viewer_close",

  // Image Viewer
  ImageViewerOpen = "image_viewer_open",
  ImageViewerClose = "image_viewer_close",

  // About Note / Edit Created Date
  AboutNoteModalView = "about_note_modal_view",
  EditNoteCreatedDateOpen = "edit_note_created_date_open",
  EditNoteCreatedDateCommit = "edit_note_created_date_commit",
  EditNoteCreatedTimeOpen = "edit_note_created_time_open",
  EditNoteCreatedTimeCommit = "edit_note_created_time_commit",

  // PDF Upload
  PdfUploadStart = "pdf_upload_start",
  PdfUploadValidationFailed = "pdf_upload_validation_failed",
  PdfUploadSuccess = "pdf_upload_success",
  PdfUploadFailed = "pdf_upload_failed",
  PdfPageLimitWarningShown = "pdf_page_limit_warning_shown",
  PdfPageLimitWarningConfirmed = "pdf_page_limit_warning_confirmed",
  PdfPageLimitWarningCancelled = "pdf_page_limit_warning_cancelled",
}

export enum TrackedEventContext {
  FloatingBar = "floating_bar",
  Notes = "notes",
  InsertMenu = "insert_menu",
}

// Monetization contexts for paywall-related events
export enum PaywallContext {
  NoteCreate = "note_create",
  NoteMentionCreate = "note_mention_create",
  ChatMessageSend = "chat_message_send",
  NativeChatMessageSend = "native_chat_message_send",
  NoteList = "note_list",
  CollectionList = "collection_list",
  DarkModeToggle = "dark_mode_toggle",
  ChatModelSelection = "chat_model_selection",
  ContentUnderstandingPdfPage = "content_understanding_pdf_page",
  Proactive = "proactive",
}

export type FeatureGateLevel = "warning" | "exceeded";

export interface MonetizationUsageEventParams {
  note_create_quota_used?: number;
  note_create_quota_limit?: number | typeof Infinity;
  note_create_usage_ratio?: number;
  chat_message_send_quota_used?: number;
  chat_message_send_quota_limit?: number | typeof Infinity;
  chat_message_send_chat_usage_ratio?: number;
  native_chat_message_send_quota_used?: number;
  native_chat_message_send_quota_limit?: number | typeof Infinity;
  native_chat_message_send_usage_ratio?: number;
}

export interface FeatureGateEventParams {
  feature: GatedFeature;
  level: FeatureGateLevel;
  used: number;
  limit: number | typeof Infinity;
  warn_at: number | null;
}

export interface PaywallEventParams
  extends MonetizationUsageEventParams, Partial<FeatureGateEventParams> {
  context?: PaywallContext | string;
  source?: ProactivePaywallSource;
  current_plan_kind?: SubscriptionPlanKind;
  effective_plan_kind?: SubscriptionPlanKind;
  original_plan_kind?: SubscriptionPlanKind;
  original_effective_plan_kind?: SubscriptionPlanKind;
  target_plan_kind?: SubscriptionPlanKind;
  target_plan_variant?: SubscriptionPlanVariant;
  experiment_key?: string;
  experiment_variant_key?: string;
  commercial_catalog_generation?: string;
  commercial_plan_key?: string;
  current_commercial_plan_key?: string;
  target_commercial_plan_key?: string;
  commercial_upgrade_action?: "review_plans" | "select_plan";
  stripe_session_id?: string;
  failure_stage?: string;
  failure_reason?: string;
  error_message?: string;
}

export interface FloatingAgentProactiveTrialBannerEventParams {
  source: "floating_agent_proactive_trial_banner";
  current_plan_kind: SubscriptionPlanKind;
  effective_plan_kind: SubscriptionPlanKind;
  target_plan_kind: Extract<SubscriptionPlanKind, "proactive">;
  is_proactive_trial_eligible: boolean;
  proactive_trial_banner_is_dismissed: boolean;
}

export interface FloatingAgentUpgradeButtonEventParams {
  source: "floating_agent_upgrade_button";
  current_plan_kind: SubscriptionPlanKind;
  effective_plan_kind: SubscriptionPlanKind;
  target_plan_kind: Extract<SubscriptionPlanKind, "proactive">;
  is_proactive_trial_eligible: boolean;
}

export interface FloatingAgentPresenceSetEventParams {
  presence: "always" | "unread" | "on_demand";
}

export type MemAgentPttMode = "ptt" | "hands-free";

export interface MemAgentPttStartEventParams {
  mode: MemAgentPttMode;
}

export interface MemAgentPttEndEventParams extends MemAgentPttStartEventParams {
  transcript: string;
  duration: number;
  /** How the capture resolved — "transcript" sent live, "audio-pending"
   * queued for deferred transcription, "empty" discarded. */
  result: "transcript" | "audio-pending" | "empty";
}

export interface FeatureGateDeniedEventParams extends FeatureGateEventParams {
  context?: PaywallContext | string;
}

export type AppCalloutBannerActionSlot = "inline" | "primary" | "secondary" | "tertiary";

export interface AppCalloutBannerEventParams {
  space_account_app_callout_id: string;
  app_callout_id: string;
  is_dismissible: boolean;
  original_plan_kind?: SubscriptionPlanKind;
  original_effective_plan_kind?: SubscriptionPlanKind;
}

export interface AppCalloutBannerActionClickedEventParams extends AppCalloutBannerEventParams {
  action_slot: AppCalloutBannerActionSlot;
  url: string;
}

export interface AppCalloutBannerDismissedEventParams extends AppCalloutBannerEventParams {
  url: string | null;
}

export type MemAgentSlackConnectSource = "settings" | "proactive_checkout";
export type MemAgentSlackRedirectMethod = "direct" | "auto" | "fallback";
export type MemAgentSlackConnectionFailureReason =
  | "desktop_redirect_failed"
  | "ios_redirect_failed"
  | "state_mismatch"
  | "oauth_error"
  | "not_connected_after_reload"
  | "unknown_error";

export interface MemAgentSlackConnectionContextEventParams {
  source: MemAgentSlackConnectSource;
  is_setup: boolean;
  original_plan_kind?: SubscriptionPlanKind;
  original_effective_plan_kind?: SubscriptionPlanKind;
}

export interface MemAgentSlackConnectRedirectEventParams extends MemAgentSlackConnectionContextEventParams {
  redirect_method: MemAgentSlackRedirectMethod;
}

export interface MemAgentSlackConnectCompleteEventParams extends MemAgentSlackConnectionContextEventParams {
  slack_workspace_id: string | null;
  slack_team_name: string | null;
}

export type MemAgentSlackConnectedModalViewEventParams = MemAgentSlackConnectCompleteEventParams;

export interface MemAgentSlackConnectFailedEventParams extends MemAgentSlackConnectionContextEventParams {
  failure_reason: MemAgentSlackConnectionFailureReason;
  error_message?: string;
}

export type MemAgentConnectionChannel = "whatsapp" | "imessage";
export type MemAgentChannelConnectionFailureReason =
  | "activation_request_failed"
  | "activation_expired";
export type MemAgentChannelConnectionDialogState =
  | "loading"
  | "waiting"
  | "success"
  | "failed"
  | "expired";

export interface MemAgentChannelConnectionEventParams {
  channel: MemAgentConnectionChannel;
  source: "settings";
  attempt_number: number;
}

export type MemAgentChannelConnectAttemptEventParams = MemAgentChannelConnectionEventParams;
export type MemAgentChannelConnectActivationReadyEventParams = MemAgentChannelConnectionEventParams;
export type MemAgentChannelConnectOpenEventParams = MemAgentChannelConnectionEventParams;
export type MemAgentChannelConnectCompleteEventParams = MemAgentChannelConnectionEventParams;

export interface MemAgentChannelConnectFailedEventParams extends MemAgentChannelConnectionEventParams {
  failure_reason: MemAgentChannelConnectionFailureReason;
}

export interface MemAgentChannelConnectDialogClosedEventParams extends MemAgentChannelConnectionEventParams {
  dialog_state: MemAgentChannelConnectionDialogState;
}

export interface MemAgentIMessageLeadClaimEventParams {
  source: "login_url";
}

export interface MemAgentIMessageLeadClaimCompleteEventParams extends MemAgentIMessageLeadClaimEventParams {
  is_enabled: boolean;
}

export interface MemAgentIMessageLeadClaimFailedEventParams extends MemAgentIMessageLeadClaimEventParams {
  failure_reason: "claim_request_failed";
}

export type MemAgentShareItemKind = "skill" | "routine";

export interface MemAgentShareOwnerEventParams {
  item_kind: MemAgentShareItemKind;
  item_id: string;
}

export interface MemAgentShareRecipientEventParams {
  item_kind: MemAgentShareItemKind;
}

export interface MemAgentShareLinkViewEventParams extends MemAgentShareRecipientEventParams {
  /** True when the recipient already saved this shared item. */
  already_saved: boolean;
}

export type MemAgentSkillCreateSource = "editor" | "share_import";
export type MemAgentSkillUpdateKind = "content" | "status";

export interface MemAgentSkillLifecycleEventParams {
  skill_id: string;
}

export interface MemAgentSkillCreateEventParams extends MemAgentSkillLifecycleEventParams {
  source: MemAgentSkillCreateSource;
}

export interface MemAgentSkillUpdateEventParams extends MemAgentSkillLifecycleEventParams {
  update_kind: MemAgentSkillUpdateKind;
}

export interface MemAgentSlackWorkspaceEventParams {
  slack_workspace_id: string | null;
  slack_team_name: string | null;
}

export interface MemAgentSettingsPageViewEventParams extends MemAgentSlackWorkspaceEventParams {
  source: "settings";
  is_setup: boolean;
  is_slack_account_connected: boolean;
  is_slack_connected: boolean;
  original_plan_kind: SubscriptionPlanKind;
  original_effective_plan_kind: SubscriptionPlanKind;
}

export type MemAgentOverlaySurfaceId = Exclude<AgentSurfaceId, AgentSurfaceId.Overview>;
type MemAgentOverlayListSurfaceId = Exclude<
  MemAgentOverlaySurfaceId,
  AgentSurfaceId.Project | AgentSurfaceId.ProjectTasks | AgentSurfaceId.Task
>;

export type MemAgentOverlayEntryPoint = "top_bar" | "floating_agent";

export type MemAgentOverlayEntryClickEventParams = {
  entry_point: MemAgentOverlayEntryPoint;
} & (
  | {
      task_count: number;
      project_count: number;
    }
  | {
      task_count?: never;
      project_count?: never;
    }
);

export type MemAgentOverlaySurfaceContextEventParams =
  | {
      surface_id: AgentSurfaceId.Project | AgentSurfaceId.ProjectTasks;
      project_id: string;
      task_id?: never;
    }
  | {
      surface_id: AgentSurfaceId.Task;
      project_id?: never;
      task_id: string;
    }
  | {
      surface_id: MemAgentOverlayListSurfaceId;
      project_id?: never;
      task_id?: never;
    };

export type MemAgentOverlayViewEventParams = MemAgentOverlaySurfaceContextEventParams;

export type MemAgentOverlayCloseEventParams = MemAgentOverlaySurfaceContextEventParams & {
  session_duration_ms: number;
};

export type MemAgentOverlayItemKind =
  | "project"
  | "task"
  | "follow_up"
  | "routine"
  | "note"
  | "collection";

export type MemAgentOverlayItemSource =
  | "project_card"
  | "project_next_up"
  | "task_row"
  | "task_project"
  | "task_source"
  | "activity_reference";

export type MemAgentOverlayItemClickEventParams = MemAgentOverlaySurfaceContextEventParams & {
  item_kind: MemAgentOverlayItemKind;
  item_id: string;
  source: MemAgentOverlayItemSource;
};

export type MemAgentOverlayFilterKind =
  | "sort"
  | "view"
  | "status"
  | "project"
  | "entity"
  | "search";

export type MemAgentOverlayFilterChangeEventParams = MemAgentOverlaySurfaceContextEventParams &
  (
    | {
        filter_kind: Exclude<MemAgentOverlayFilterKind, "search">;
        filter_value: string;
      }
    | {
        filter_kind: "search";
        query_length: number;
      }
  );

export type MemAgentOverlayTaskStatusAction = "completed" | "reopened" | "marked_incomplete";

type MemAgentOverlayTaskStatusSurfaceContext =
  | {
      surface_id: AgentSurfaceId.Project | AgentSurfaceId.ProjectTasks;
      project_id: string;
    }
  | {
      surface_id: Exclude<
        MemAgentOverlaySurfaceId,
        AgentSurfaceId.Project | AgentSurfaceId.ProjectTasks
      >;
      project_id?: never;
    };

export type MemAgentOverlayTaskStatusToggleEventParams = MemAgentOverlayTaskStatusSurfaceContext & {
  task_id: string;
  action: MemAgentOverlayTaskStatusAction;
};

export type MemAgentLearnMoreClickEventParams = MemAgentSettingsPageViewEventParams;

export interface MemAgentSlackOpenClickEventParams extends MemAgentSlackWorkspaceEventParams {
  source: "settings" | "connected_modal";
}

export type RemindersDeliveryOption = "Slack" | "Teams" | "iMessage" | "WhatsApp";

export type RemindersUnavailableDeliveryOption = Exclude<RemindersDeliveryOption, "Slack">;

export type MemAgentSettingsSource = "settings" | "editor_reminder_shill";

export type ProactivePaywallEntrySource = Exclude<MemAgentSettingsSource, "settings">;

export type ProactivePaywallSource = ProactivePaywallEntrySource | "onboarding" | "imessage_claim";

export type RemindersCalloutVisibleEventParams = AppCalloutBannerEventParams;

export interface RemindersSettingsPageVisibleEventParams {
  source: MemAgentSettingsSource;
  user_email: string;
}

export interface RemindersEnableClickedEventParams {
  source: MemAgentSettingsSource;
  user_email: string;
  option: RemindersDeliveryOption;
}

export interface ReminderShillSetupDialogVisibleEventParams {
  source: ProactivePaywallSource;
  user_email: string;
  subscription_plan_kind: SubscriptionPlanKind;
  note_id: string;
  reminder_shill_id: string;
  reminder_shill_text: string;
}

export interface EditorReminderShillEventParams {
  context: "editor_reminder_shill";
  note_id: string;
  subscription_plan_kind: SubscriptionPlanKind;
  reminder_shill_id: string;
  reminder_shill_text: string;
  reminder_shill_event_source: string;
  reminder_shill_generated_at: string;
  reminder_shill_generated_from_note_model_version: number;
}

export interface VoiceSessionEventParams {
  audio_recording_id: string;
  note_id: string | null;
  recording_strategy: AudioRecordingStrategy | null;
  has_calendar_event_source: boolean;
  is_meeting_recording: boolean;
}

// Tab Navigation Event Parameters
export interface PaneSplitEventParams {
  origin_pane_index: number | string;
}

export interface TabOpenEventParams {
  view_id: string;
  pane_index: number;
}

export interface TabViewEventParams {
  view_id: string;
  pane_index: number;
}

export interface ExperimentStartedEventParams {
  experiment: string;
  variant: string;
  context?: Record<string, unknown>;
}

export interface SidePanelViewEventParams {
  variant: "control" | "treatment";
}

// Getting Started Event Parameters
export interface GettingStartedStepCompletedEventParams {
  step_id: string;
  step_title: string;
  completed_count: number;
  total_count: number;
}

export interface GettingStartedToggledEventParams {
  is_expanded: boolean;
}

// OneClick Import Event Parameters
export interface OneClickImportListViewEventParams {
  source: OneClickImportSource;
  total_notes_count: number;
  selectable_notes_count: number;
  locked_notes_count: number;
  entry_point: OneClickImportEntryPoint;
}

export interface OneClickImportItemSelectEventParams {
  source: OneClickImportSource;
  note_title: string;
  note_subtitle: string | undefined;
  selected_count: number;
  total_count: number;
  entry_point: OneClickImportEntryPoint;
}

export interface OneClickImportItemDeselectEventParams {
  source: OneClickImportSource;
  note_title: string;
  note_subtitle: string | undefined;
  selected_count: number;
  total_count: number;
  entry_point: OneClickImportEntryPoint;
}

export interface OneClickImportCancelEventParams {
  source: OneClickImportSource;
  selected_count: number;
  total_count: number;
  entry_point: OneClickImportEntryPoint;
}

export interface OneClickImportSubmitEventParams {
  source: OneClickImportSource;
  selected_count: number;
  total_count: number;
  use_folders_as_collections: boolean;
  use_tags_as_collections: boolean;
  /** Total number of folders available in imported notes */
  total_folders_count: number;
  /** Number of folders user selected to convert to collections */
  selected_folders_count: number;
  /** Total number of tags available in imported notes */
  total_tags_count: number;
  /** Number of tags user selected to convert to collections */
  selected_tags_count: number;
  entry_point: OneClickImportEntryPoint;
}

export interface OneClickImportCompleteEventParams {
  source: OneClickImportSource;
  /** Whether the import succeeded */
  success: boolean;
  /** Number of notes that were imported */
  imported_count: number;
  /** Total time from import start to completion in milliseconds */
  duration_ms: number;
  /** Number of notes that had image attachments */
  notes_with_images_count: number;
  /** Number of notes that had non-image file attachments */
  notes_with_files_count: number;
  /** Total number of image attachments across all imported notes */
  total_images_count: number;
  /** Total number of non-image file attachments across all imported notes */
  total_files_count: number;
  entry_point: OneClickImportEntryPoint;
}

export interface OneClickImportLoadFailedEventParams {
  source: OneClickImportSource;
  entry_point: OneClickImportEntryPoint;
  /** Error message from the failed load attempt */
  error: string;
}

export type CalendarProvider = "google" | "outlook";
export type CalendarConnectSource = "ios" | "web" | "desktop";
export type CalendarConnectErrorType =
  | "oauth_denied"
  | "missing_code"
  | "state_mismatch"
  | "api_error";
export type CalendarSyncStatus = "SYNCING" | "TRYING_TO_CONNECT" | "SYNCED" | "FAILED";
export type { MeetingProvider } from "@/domains/calendar/getMeetingPlatformFromUrl";

export interface CalendarConnectStartEventParams {
  provider: CalendarProvider;
  source: CalendarConnectSource;
}

export interface CalendarConnectCompleteEventParams {
  provider: CalendarProvider;
  calendar_connection_id: string;
  source: CalendarConnectSource;
}

export interface CalendarConnectFailedEventParams {
  provider: CalendarProvider;
  error_type: CalendarConnectErrorType;
  error_message?: string;
  error_subcode?: string;
  error_description?: string;
  source: CalendarConnectSource;
}

export interface CalendarDisconnectStartEventParams {
  provider: CalendarProvider;
  calendar_connection_id: string;
}

export interface CalendarDisconnectCompleteEventParams {
  provider: CalendarProvider;
  calendar_connection_id: string;
}

export interface CalendarToggleEventParams {
  provider: CalendarProvider;
  connected_calendar_id: string;
  calendar_connection_id: string;
  enabled: boolean;
}

export interface CalendarRefreshManualEventParams {
  provider: CalendarProvider;
  connected_calendar_id: string;
  calendar_connection_id: string;
  previous_sync_status: CalendarSyncStatus;
}

export interface NoteLinkToCalendarEventParams {
  note_id: string;
  connected_calendar_event_id: string;
  context: EventContext;
}

export interface CalendarEventJoinEventParams {
  connected_calendar_event_id: string;
  meeting_provider: MeetingProvider;
}

export interface CalendarEventOpenEventParams {
  connected_calendar_event_id: string;
}

export interface ChatModelSelectEventParams {
  selectedModel: string;
  previousModel: string;
  startsNewChat: boolean;
  choseOutOfTierModel: boolean;
}

export interface ChatModelDropdownOpenEventParams {
  currentModel: string;
}

export interface ChatGlobalInstructionsSaveEventParams {
  instructions: string;
}

export interface ChatSessionMessageSendEventParams {
  session_id: string;
  message_contents: string;
  selectedModel: ChatModel;
  model_family: ChatModelFamily;
}

export interface ChatSessionMessageFeedbackEventParams {
  event_id: string;
  session_id: string;
  event_kind: string;
  selected_model: ChatModel;
}

export interface ConversionAttributionEventParams {
  conversion_event_id: string;
  marketing_attribution_captured_at?: string;
  marketing_journey_id?: string;
  marketing_landing_path?: string;
  marketing_referrer_domain?: string;
  meta_fbc?: string;
  meta_fbclid?: string;
  meta_fbp?: string;
}

export interface AccountSignupCompletedEventParams extends ConversionAttributionEventParams {
  account_age_seconds: number;
  account_email: string;
}

export interface EmailPasswordAuthFunnelEventParams {
  step:
    | "email"
    | "password"
    | "login_password"
    | "signup_password"
    | "verification"
    | "verification_resend"
    | "password_reset_request"
    | "password_reset";
  outcome: "submitted" | "succeeded" | "failed" | "validation_failed";
  next_step?: "authenticated" | "email_sent" | "password";
  failure_reason?: "missing_input" | "weak_password" | "password_mismatch" | "request_failed";
}

export interface MobileWebLoginCompletedEventParams extends ConversionAttributionEventParams {
  account_email: string;
}

export interface MemAgentMessageSentEventParams extends ConversionAttributionEventParams {
  channel: "native_chat";
  input_method: "text" | "voice";
}

export interface ProactiveAccessEnabledEventParams extends ConversionAttributionEventParams {
  billing_provider?: string;
  checkout_source: "existing_user_onboarding" | "onboarding" | "upgrade_plan" | "upsell";
  plan_kind: "proactive";
  plan_variant?: string;
  pricing_experiment_key?: string;
  pricing_experiment_variant_key?: string;
  provider_subscription_id?: string;
  subscription_price_usd?: number;
}

export interface ChromeExtensionModelSelectEventParams {
  selectedModel: ChatModel;
  previousModel: ChatModel;
}

export interface ChromeExtensionModelDropdownOpenEventParams {
  currentModel: ChatModel;
}

export interface EditorImageHoverToolbarOpenEventParams {
  note_id: string;
  image_id: string | null;
}

export interface EditorImageHoverToolbarDownloadEventParams {
  note_id: string;
  image_id: string | null;
}

export type OcrInsertPosition = "above" | "below";

export interface OcrMenuOpenEventParams {
  image_id: string | null;
  note_id: string;
}

export interface OcrInsertClickEventParams {
  image_id: string | null;
  note_id: string;
  position: OcrInsertPosition;
}

// Meeting Briefing Event Parameters
export type MeetingBriefingNoteChipAction = "click" | "hover";

export interface MeetingBriefingViewEventParams {
  connected_calendar_event_id: string | null;
  note_ids: string[];
  markdown: string | null;
}

export interface MeetingBriefingRegenerateEventParams {
  connected_calendar_event_id: string;
  active_note_id: string;
}

export interface MeetingBriefingFeedbackSubmitEventParams {
  connected_calendar_event_id: string;
  response: "UPVOTE" | "DOWNVOTE";
  comment: string | null;
  note_ids: string[];
  markdown: string | null;
}

export interface MeetingBriefingNoteChipInteractEventParams {
  connected_calendar_event_id: string | null;
  action: MeetingBriefingNoteChipAction;
  note_id: string;
  note_title: string;
}

export interface OnboardingStepChangeEventParams {
  from_step_id: string | undefined;
  to_step_id: string | undefined;
  completed_count: number;
  total_count: number;
  onboarding_flow_version?: string;
  onboarding_intent?: string;
}

export interface OnboardingSubstepChangeEventParams {
  step_id: string | undefined;
  from_substep_id: string | undefined;
  to_substep_id: string | undefined;
  onboarding_flow_version?: string;
  onboarding_intent?: string;
}

export interface OnboardingCompleteEventParams {
  total_steps: number;
  onboarding_flow_version?: string;
  onboarding_intent?: string;
}

export interface NewUserOnboardingViewedEventParams {
  step_id: "icp-rfi";
  substep_id: "referrer";
  onboarding_version: "web_onboarding_v1";
}

export interface HeadsUpAnalyticsContextEventParams {
  context_id?: string;
  context_note_id?: string;
  context_note_title?: string;
  smart_view_id?: string;
  smart_view_kind: string;
  active_space_account_id?: string;
}

export type HeadsUpLiveSessionEndReason =
  | "recording_stopped"
  | "context_changed"
  | "note_changed"
  | "live_unavailable"
  | "unmount";

export interface HeadsUpLiveSessionStartEventParams extends HeadsUpAnalyticsContextEventParams {
  recording_id: string;
}

export interface HeadsUpLiveSessionEndEventParams extends HeadsUpAnalyticsContextEventParams {
  recording_id: string;
  duration_ms: number;
  end_reason: HeadsUpLiveSessionEndReason;
}

export interface HeadsUpLiveEntryAppearEventParams extends HeadsUpAnalyticsContextEventParams {
  smart_card_id: string;
  entry_id: string;
  note_id: string;
  note_title: string;
  topic: string;
  insight: string | null;
  item_position_index: number;
  quote: string | null;
  has_feedback_controls: boolean;
  recording_offset_seconds: number | null;
}

export type HeadsUpLiveExplainerSource = "content_card" | "empty_state";

export interface HeadsUpLiveExplainerOpenEventParams extends HeadsUpAnalyticsContextEventParams {
  source: HeadsUpLiveExplainerSource;
  smart_card_id: string | null;
  is_live_active: boolean;
}

export interface HeadsUpLiveEntryFeedbackSubmitEventParams extends HeadsUpAnalyticsContextEventParams {
  smart_card_id: string;
  entry_id: string;
  note_id: string;
  note_title: string;
  response: "UPVOTE" | "DOWNVOTE";
  comment: string | null;
  topic: string;
  insight: string | null;
  has_quote: boolean;
  recording_offset_seconds: number | null;
}

// PDF Viewer Event Parameters
export type PdfViewerOpenContext = "editor" | "search_result" | "chat";

export type PdfViewerPageChangeMethod =
  | "arrow_button"
  | "keyboard"
  | "scroll"
  | "carousel"
  | "page_input";

export interface PdfViewerOpenEventParams {
  context: PdfViewerOpenContext;
}

export interface PdfViewerPageChangeEventParams {
  context: PdfViewerOpenContext;
  page: number;
  total_pages: number;
  method: PdfViewerPageChangeMethod;
}

export interface PdfViewerEmbedToggleEventParams {
  context: PdfViewerOpenContext;
  is_visible: boolean;
}

export interface PdfViewerCloseEventParams {
  context: PdfViewerOpenContext;
  pages_viewed: number;
  total_pages: number;
  session_duration_ms: number;
}

// Image Viewer Event Parameters
export type ImageViewerOpenContext = "editor" | "search_result" | "chat";

export interface ImageViewerOpenEventParams {
  context: ImageViewerOpenContext;
}

export interface ImageViewerCloseEventParams {
  context: ImageViewerOpenContext;
  session_duration_ms: number;
}

// PDF Upload Event Parameters
export type PdfUploadSource = "drag_drop" | "file_picker" | "paste";

export type PdfUploadValidationReason = "too_large" | "invalid_mime" | "page_limit";

export interface PdfUploadStartEventParams {
  context: PdfViewerOpenContext;
  source: PdfUploadSource;
  file_size_bytes: number;
  mime_type: string;
}

export interface PdfUploadValidationFailedEventParams {
  context: PdfViewerOpenContext;
  reason: PdfUploadValidationReason;
  file_size_bytes?: number;
  limit_bytes?: number;
}

export interface PdfUploadSuccessEventParams {
  context: PdfViewerOpenContext;
  file_size_bytes: number;
  duration_ms: number;
  attachment_id: string;
}

export interface PdfUploadFailedEventParams {
  context: PdfViewerOpenContext;
  file_size_bytes: number;
  duration_ms: number;
  error_code: string;
  http_status?: number;
}

export interface PdfPageLimitWarningShownEventParams {
  page_count: number;
  page_limit: number;
}

export interface PdfPageLimitWarningConfirmedEventParams {
  page_count: number;
  page_limit: number;
  visible_duration_ms: number;
}

export interface PdfPageLimitWarningCancelledEventParams {
  page_count: number;
  page_limit: number;
  visible_duration_ms: number;
}

// About Note Modal Event Parameters
export type AboutNoteModalEntryPoint = "extra_actions_dropdown" | "collection_overflow";

export interface AboutNoteModalViewEventParams {
  note_id: string;
  entry_point: AboutNoteModalEntryPoint;
  can_edit_created_at: boolean;
}

export interface EditNoteCreatedDateEventParams {
  note_id: string;
}

export interface EditNoteCreatedDateCommitEventParams {
  note_id: string;
  previous_locally_created_at: string;
  next_locally_created_at: string;
}

export interface EditNoteCreatedTimeEventParams {
  note_id: string;
}

export interface EditNoteCreatedTimeCommitEventParams {
  note_id: string;
  previous_locally_created_at: string;
  next_locally_created_at: string;
}

export interface KeyboardShortcutPressedEventParams {
  shortcut: string;
}

// MaaC (Mem-as-a-Collaborator) Phase 1 Event Parameters
export interface MemAttributionEditableToggleEventParams {
  noteId: string;
  previousValue: boolean;
  newValue: boolean;
}

export interface NoteShareSheetOpenEventParams {
  noteId: string;
  context: EventContext;
  hasAnyMemAttribution: boolean;
  mostRecentEditorWasMem: boolean;
  currentIsEditableByAiValue: boolean;
}

/**
 * Type-safe mapping of TrackedEvent to their required properties.
 * Events not listed here will fall back to Record<string, unknown>.
 */
export type TrackedEventProperties = {
  [TrackedEvent.AppCalloutBannerView]: AppCalloutBannerEventParams;
  [TrackedEvent.AppCalloutBannerActionClicked]: AppCalloutBannerActionClickedEventParams;
  [TrackedEvent.AppCalloutBannerDismissed]: AppCalloutBannerDismissedEventParams;
  [TrackedEvent.PaywallView]: PaywallEventParams;
  [TrackedEvent.PaywallCancelClick]: PaywallEventParams;
  [TrackedEvent.PaywallUpgradeClick]: PaywallEventParams;
  [TrackedEvent.PaywallUpgradeReturn]: PaywallEventParams;
  [TrackedEvent.PaywallUpgradeFailed]: PaywallEventParams;
  [TrackedEvent.FeatureGateBannerView]: FeatureGateEventParams;
  [TrackedEvent.FeatureGateBannerUpgradeClick]: FeatureGateEventParams;
  [TrackedEvent.FloatingAgentProactiveTrialBannerView]: FloatingAgentProactiveTrialBannerEventParams;
  [TrackedEvent.FloatingAgentProactiveTrialBannerClicked]: FloatingAgentProactiveTrialBannerEventParams;
  [TrackedEvent.FloatingAgentProactiveTrialBannerDismissed]: FloatingAgentProactiveTrialBannerEventParams;
  [TrackedEvent.FloatingAgentUpgradeButtonClicked]: FloatingAgentUpgradeButtonEventParams;
  [TrackedEvent.FloatingAgentPresenceSet]: FloatingAgentPresenceSetEventParams;
  [TrackedEvent.MemAgentPttStart]: MemAgentPttStartEventParams;
  [TrackedEvent.MemAgentPttEnd]: MemAgentPttEndEventParams;
  [TrackedEvent.FeatureGateDenied]: FeatureGateDeniedEventParams;
  [TrackedEvent.AccountSignupCompleted]: AccountSignupCompletedEventParams;
  [TrackedEvent.EmailPasswordAuthFunnel]: EmailPasswordAuthFunnelEventParams;
  [TrackedEvent.MobileWebLoginCompleted]: MobileWebLoginCompletedEventParams;
  [TrackedEvent.MemAgentMessageSent]: MemAgentMessageSentEventParams;
  [TrackedEvent.ProactiveAccessEnabled]: ProactiveAccessEnabledEventParams;
  [TrackedEvent.MemAgentOverlayEntryClick]: MemAgentOverlayEntryClickEventParams;
  [TrackedEvent.MemAgentOverlayView]: MemAgentOverlayViewEventParams;
  [TrackedEvent.MemAgentOverlayClose]: MemAgentOverlayCloseEventParams;
  [TrackedEvent.MemAgentOverlayItemClick]: MemAgentOverlayItemClickEventParams;
  [TrackedEvent.MemAgentOverlayFilterChange]: MemAgentOverlayFilterChangeEventParams;
  [TrackedEvent.MemAgentOverlayTaskStatusToggle]: MemAgentOverlayTaskStatusToggleEventParams;
  [TrackedEvent.MemAgentSettingsPageView]: MemAgentSettingsPageViewEventParams;
  [TrackedEvent.MemAgentSlackConnectRedirect]: MemAgentSlackConnectRedirectEventParams;
  [TrackedEvent.MemAgentSlackConnectComplete]: MemAgentSlackConnectCompleteEventParams;
  [TrackedEvent.MemAgentSlackConnectFailed]: MemAgentSlackConnectFailedEventParams;
  [TrackedEvent.MemAgentSlackConnectedModalView]: MemAgentSlackConnectedModalViewEventParams;
  [TrackedEvent.MemAgentSlackDisconnect]: MemAgentSlackWorkspaceEventParams;
  [TrackedEvent.MemAgentSlackOpenClick]: MemAgentSlackOpenClickEventParams;
  [TrackedEvent.MemAgentLearnMoreClick]: MemAgentLearnMoreClickEventParams;
  [TrackedEvent.MemAgentChannelConnectAttempt]: MemAgentChannelConnectAttemptEventParams;
  [TrackedEvent.MemAgentChannelConnectActivationReady]: MemAgentChannelConnectActivationReadyEventParams;
  [TrackedEvent.MemAgentChannelConnectOpen]: MemAgentChannelConnectOpenEventParams;
  [TrackedEvent.MemAgentChannelConnectComplete]: MemAgentChannelConnectCompleteEventParams;
  [TrackedEvent.MemAgentChannelConnectFailed]: MemAgentChannelConnectFailedEventParams;
  [TrackedEvent.MemAgentChannelConnectDialogClosed]: MemAgentChannelConnectDialogClosedEventParams;
  [TrackedEvent.MemAgentIMessageLeadClaimStart]: MemAgentIMessageLeadClaimEventParams;
  [TrackedEvent.MemAgentIMessageLeadClaimComplete]: MemAgentIMessageLeadClaimCompleteEventParams;
  [TrackedEvent.MemAgentIMessageLeadClaimFailed]: MemAgentIMessageLeadClaimFailedEventParams;
  [TrackedEvent.MemAgentShareDialogOpen]: MemAgentShareOwnerEventParams;
  [TrackedEvent.MemAgentShareLinkCreate]: MemAgentShareOwnerEventParams;
  [TrackedEvent.MemAgentShareLinkCopy]: MemAgentShareOwnerEventParams;
  [TrackedEvent.MemAgentShareLinkRevoke]: MemAgentShareOwnerEventParams;
  [TrackedEvent.MemAgentShareLinkView]: MemAgentShareLinkViewEventParams;
  [TrackedEvent.MemAgentShareLinkUnavailable]: MemAgentShareRecipientEventParams;
  [TrackedEvent.MemAgentShareAccept]: MemAgentShareRecipientEventParams;
  [TrackedEvent.MemAgentShareAcceptFailed]: MemAgentShareRecipientEventParams;
  [TrackedEvent.MemAgentSkillCreate]: MemAgentSkillCreateEventParams;
  [TrackedEvent.MemAgentSkillUpdate]: MemAgentSkillUpdateEventParams;
  [TrackedEvent.MemAgentSkillDelete]: MemAgentSkillLifecycleEventParams;
  [TrackedEvent.RemindersCalloutVisible]: RemindersCalloutVisibleEventParams;
  [TrackedEvent.RemindersSettingsPageVisible]: RemindersSettingsPageVisibleEventParams;
  [TrackedEvent.RemindersEnableClicked]: RemindersEnableClickedEventParams;
  [TrackedEvent.ReminderShillSetupDialogVisible]: ReminderShillSetupDialogVisibleEventParams;
  [TrackedEvent.EditorReminderShillPillVisible]: EditorReminderShillEventParams;
  [TrackedEvent.EditorReminderShillPillDismissed]: EditorReminderShillEventParams;
  [TrackedEvent.EditorReminderShillPillSettingsLaunched]: EditorReminderShillEventParams;
  [TrackedEvent.VoiceSessionStart]: VoiceSessionEventParams;
  [TrackedEvent.VoiceSessionPause]: VoiceSessionEventParams;
  [TrackedEvent.VoiceSessionResume]: VoiceSessionEventParams;
  [TrackedEvent.VoiceSessionDone]: VoiceSessionEventParams;
  [TrackedEvent.ExperimentStarted]: ExperimentStartedEventParams;
  [TrackedEvent.PaneSplit]: PaneSplitEventParams;
  [TrackedEvent.TabOpen]: TabOpenEventParams;
  [TrackedEvent.TabView]: TabViewEventParams;
  [TrackedEvent.SidePanelView]: SidePanelViewEventParams;
  [TrackedEvent.GettingStartedStepCompleted]: GettingStartedStepCompletedEventParams;
  [TrackedEvent.GettingStartedToggled]: GettingStartedToggledEventParams;
  [TrackedEvent.OneClickImportListView]: OneClickImportListViewEventParams;
  [TrackedEvent.OneClickImportItemSelect]: OneClickImportItemSelectEventParams;
  [TrackedEvent.OneClickImportItemDeselect]: OneClickImportItemDeselectEventParams;
  [TrackedEvent.OneClickImportCancel]: OneClickImportCancelEventParams;
  [TrackedEvent.OneClickImportSubmit]: OneClickImportSubmitEventParams;
  [TrackedEvent.OneClickImportComplete]: OneClickImportCompleteEventParams;
  [TrackedEvent.OneClickImportLoadFailed]: OneClickImportLoadFailedEventParams;
  [TrackedEvent.CalendarConnectStart]: CalendarConnectStartEventParams;
  [TrackedEvent.CalendarConnectComplete]: CalendarConnectCompleteEventParams;
  [TrackedEvent.CalendarConnectFailed]: CalendarConnectFailedEventParams;
  [TrackedEvent.CalendarDisconnectStart]: CalendarDisconnectStartEventParams;
  [TrackedEvent.CalendarDisconnectComplete]: CalendarDisconnectCompleteEventParams;
  [TrackedEvent.CalendarToggle]: CalendarToggleEventParams;
  [TrackedEvent.CalendarRefreshManual]: CalendarRefreshManualEventParams;
  [TrackedEvent.NoteLinkToCalendarEvent]: NoteLinkToCalendarEventParams;
  [TrackedEvent.CalendarEventJoin]: CalendarEventJoinEventParams;
  [TrackedEvent.CalendarEventOpen]: CalendarEventOpenEventParams;
  [TrackedEvent.CalendarIntegrationShillHide]: Record<string, never>;
  [TrackedEvent.ChatModelSelect]: ChatModelSelectEventParams;
  [TrackedEvent.ChatModelDropdownOpen]: ChatModelDropdownOpenEventParams;
  [TrackedEvent.ChatGlobalInstructionsSave]: ChatGlobalInstructionsSaveEventParams;
  [TrackedEvent.ChromeExtensionModelSelect]: ChromeExtensionModelSelectEventParams;
  [TrackedEvent.ChromeExtensionModelDropdownOpen]: ChromeExtensionModelDropdownOpenEventParams;
  [TrackedEvent.EditorImageHoverToolbarOpen]: EditorImageHoverToolbarOpenEventParams;
  [TrackedEvent.EditorImageHoverToolbarDownload]: EditorImageHoverToolbarDownloadEventParams;
  [TrackedEvent.OcrMenuOpen]: OcrMenuOpenEventParams;
  [TrackedEvent.OcrInsertClick]: OcrInsertClickEventParams;
  [TrackedEvent.ChatSessionMessageSend]: ChatSessionMessageSendEventParams;
  [TrackedEvent.ChatSessionMessageUpvote]: ChatSessionMessageFeedbackEventParams;
  [TrackedEvent.ChatSessionMessageDownvote]: ChatSessionMessageFeedbackEventParams;
  [TrackedEvent.MeetingBriefingView]: MeetingBriefingViewEventParams;
  [TrackedEvent.MeetingBriefingRegenerate]: MeetingBriefingRegenerateEventParams;
  [TrackedEvent.MeetingBriefingFeedbackSubmit]: MeetingBriefingFeedbackSubmitEventParams;
  [TrackedEvent.MeetingBriefingNoteChipInteract]: MeetingBriefingNoteChipInteractEventParams;
  [TrackedEvent.HeadsUpLiveSessionStart]: HeadsUpLiveSessionStartEventParams;
  [TrackedEvent.HeadsUpLiveSessionEnd]: HeadsUpLiveSessionEndEventParams;
  [TrackedEvent.HeadsUpLiveEntryAppear]: HeadsUpLiveEntryAppearEventParams;
  [TrackedEvent.HeadsUpLiveExplainerOpen]: HeadsUpLiveExplainerOpenEventParams;
  [TrackedEvent.HeadsUpLiveEntryFeedbackSubmit]: HeadsUpLiveEntryFeedbackSubmitEventParams;
  [TrackedEvent.NewUserOnboardingViewed]: NewUserOnboardingViewedEventParams;
  [TrackedEvent.OnboardingStepChange]: OnboardingStepChangeEventParams;
  [TrackedEvent.OnboardingSubstepChange]: OnboardingSubstepChangeEventParams;
  [TrackedEvent.OnboardingComplete]: OnboardingCompleteEventParams;
  [TrackedEvent.PdfViewerOpen]: PdfViewerOpenEventParams;
  [TrackedEvent.PdfViewerPageChange]: PdfViewerPageChangeEventParams;
  [TrackedEvent.PdfViewerEmbedToggle]: PdfViewerEmbedToggleEventParams;
  [TrackedEvent.PdfViewerClose]: PdfViewerCloseEventParams;
  [TrackedEvent.ImageViewerOpen]: ImageViewerOpenEventParams;
  [TrackedEvent.ImageViewerClose]: ImageViewerCloseEventParams;
  [TrackedEvent.PdfUploadStart]: PdfUploadStartEventParams;
  [TrackedEvent.PdfUploadValidationFailed]: PdfUploadValidationFailedEventParams;
  [TrackedEvent.PdfUploadSuccess]: PdfUploadSuccessEventParams;
  [TrackedEvent.PdfUploadFailed]: PdfUploadFailedEventParams;
  [TrackedEvent.PdfPageLimitWarningShown]: PdfPageLimitWarningShownEventParams;
  [TrackedEvent.PdfPageLimitWarningConfirmed]: PdfPageLimitWarningConfirmedEventParams;
  [TrackedEvent.PdfPageLimitWarningCancelled]: PdfPageLimitWarningCancelledEventParams;
  [TrackedEvent.AboutNoteModalView]: AboutNoteModalViewEventParams;
  [TrackedEvent.EditNoteCreatedDateOpen]: EditNoteCreatedDateEventParams;
  [TrackedEvent.EditNoteCreatedDateCommit]: EditNoteCreatedDateCommitEventParams;
  [TrackedEvent.EditNoteCreatedTimeOpen]: EditNoteCreatedTimeEventParams;
  [TrackedEvent.EditNoteCreatedTimeCommit]: EditNoteCreatedTimeCommitEventParams;
  [TrackedEvent.KeyboardShortcutPressed]: KeyboardShortcutPressedEventParams;
  [TrackedEvent.MemAttributionEditableToggle]: MemAttributionEditableToggleEventParams;
  [TrackedEvent.NoteShareSheetOpen]: NoteShareSheetOpenEventParams;
};

/**
 * Helper type to get properties for a specific TrackedEvent.
 * Falls back to Record<string, unknown> for events without specific properties.
 */
export type PropertiesForEvent<T extends TrackedEvent> = T extends keyof TrackedEventProperties
  ? TrackedEventProperties[T]
  : Record<string, unknown>;
